import {Box, Divider, Grid, Link, Typography} from "@mui/material";
import {RequestTracker} from "../../../../Component/Model/Dto/Staging/RequestTracker";
import {RequestStatus} from "../../../../Component/Model/Enum/ImportEnum";
import {StringDateTimeHelper} from "../../../../Component/Model/helpers";
import React from "react";
import {CancelOutlined, CheckCircleOutlineRounded, InfoOutlined, WarningAmberOutlined} from "@mui/icons-material";

interface IProps {
    selectedOrganisation: number;
    requestTracker?: RequestTracker.RequestTrackerDetail;
    requestEmployees: () => void;
    openPayrollIImport: () => void;
}

export const RequestTrackerContent: React.FC<IProps> =
    ({
         selectedOrganisation,
         requestTracker,
         requestEmployees,
         openPayrollIImport,


     }) => {

        const getStatusProps = () => {
            const status = requestTracker?.status;
            if (!selectedOrganisation || selectedOrganisation <= 0 || !requestTracker)
                return {color: "warning", icon: <WarningAmberOutlined color="warning" sx={{width: 60, height: 60}}/>};

            if (failedStatuses.includes(status ?? -1) || requestTracker?.status === RequestStatus.Expired)
                return {color: "error", icon: <CancelOutlined color="error" sx={{width: 60, height: 60}}/>};

            if (inProgressStatuses.includes(status ?? -1))
                return {color: "info", icon: <InfoOutlined color="info" sx={{width: 60, height: 60}}/>};

            if (status === RequestStatus.ImportReady)
                return {
                    color: "success",
                    icon: <CheckCircleOutlineRounded color="success" sx={{width: 60, height: 60}}/>
                };

            return {color: "default", icon: <InfoOutlined sx={{width: 60, height: 60, color: "#ccc"}}/>};
        };

        const inProgressStatuses = [RequestStatus.NewRequest, RequestStatus.FetchingPayroll];
        const failedStatuses = [RequestStatus.RequestFailed, RequestStatus.FetchFailed];

        const {color, icon} = getStatusProps();

        const borderColorMap: Record<string, string> = {
            success: "#4caf50",
            info: "#097384",
            warning: "#ff9800",
            error: "#f44336",
            default: "#e0e0e0",
        };

        return (<Grid
            container
            item
            style={{
                display: 'flex',
                border: 'dashed 3px #eee',
                borderColor: borderColorMap[color],
                borderRadius: '5px',
                alignItems: 'center',
                height: 200,
                width: 550
            }}>
            <Grid item xs={3} sx={{display: 'flex', justifyContent: 'center'}}>
                {icon}
            </Grid>
            <Grid item xs={9}>
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                    {(!selectedOrganisation || selectedOrganisation <= 0 ) &&

                        <Typography variant="caption" fontWeight="bold" color="secondary">
                            Please select the organisation that you want to import data for.
                        </Typography>
                    }

                    {selectedOrganisation > 0 && !requestTracker &&

                        <>
                            <Typography variant="caption" fontWeight="bold" color="secondary">
                                Data hasn't yet been fetched from your connected payroll(s) before.
                            </Typography>
                            <Divider sx={{my: 1}}/>
                            <Link
                                component="button"
                                onClick={requestEmployees}
                                underline="always"
                                color="primary"
                                sx={{cursor: 'pointer', typography: 'caption'}}
                            >
                                Click here to fetch data
                            </Link>
                        </>

                    }

                    {selectedOrganisation > 0 && requestTracker?.status === RequestStatus.Expired &&
                        <>
                            <Typography variant="caption" fontWeight="bold" color="secondary">
                                Your data was last fetched
                                on {StringDateTimeHelper(requestTracker?.createdOn?.toString() ?? '')} and has expired.
                            </Typography>
                            <Divider sx={{my: 1}}/>
                            <Link
                                component="button"
                                onClick={requestEmployees}
                                underline="always"
                                color="primary"
                                sx={{cursor: 'pointer', typography: 'caption'}}
                            >
                                Click here to fetch data
                            </Link>
                        </>

                    }

                    {selectedOrganisation > 0 && requestTracker && requestTracker?.status !== RequestStatus.Expired &&
                        <>
                            <Typography variant="caption" fontWeight="bold" color="secondary">
                                Most recent fetch of data from your connected payroll(s):
                            </Typography>

                            <Box sx={{pl: 2}}>
                                <Typography variant="caption" display="block">
                                    • Created: {StringDateTimeHelper(requestTracker?.createdOn?.toString() ?? '')}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    • Status: {requestTracker?.statusDescription}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    • Completed:{' '}
                                    {inProgressStatuses.includes(requestTracker?.status ?? -1)
                                        ? 'In progress'
                                        : StringDateTimeHelper(requestTracker?.completedOn?.toString() ?? '')}
                                </Typography>
                            </Box>

                            <Divider sx={{my: 1}}/>

                            {(requestTracker?.stagingDetails ?? []).length > 0 &&
                                !failedStatuses.includes(requestTracker?.status ?? -1) && (
                                    <>
                                        <Typography variant="caption" fontWeight="bold" color="secondary">
                                            Total records fetched:
                                        </Typography>
                                        <Box sx={{pl: 2}}>
                                            {requestTracker?.stagingDetails?.map((m, index) => (
                                                <Typography key={index} variant="caption" display="block">
                                                    • {m.entityName}:{' '}
                                                    {inProgressStatuses.includes(requestTracker.status) ? 'In Progress' : m.totalRecords ?? 0}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </>
                                )}

                            {failedStatuses.includes(requestTracker?.status ?? -1) && (
                                <Link
                                    component="button"
                                    onClick={openPayrollIImport}
                                    underline="always"
                                    color="primary"
                                    sx={{cursor: 'pointer', typography: 'caption', mt: 1}}
                                >
                                    View details
                                </Link>
                            )}
                        </>
                    }
                </Box>
            </Grid>
        </Grid>)

    };
